/**
 * League scoring and roster derivation (FR-2, FR-21).
 *
 * Nothing here is hardcoded to a format. Sleeper hands us a `scoring_settings`
 * map and a `roster_positions` array; everything the engine needs about the
 * league — PPR value, TE premium, superflex, starter counts, bench depth — is
 * derived from those two structures (A1: "never hardcode").
 *
 * Pure module — no I/O.
 */

import type { LeagueConfig, Position, ScoringFormat, StatLine } from './types.ts';
import { FANTASY_POSITIONS } from './types.ts';

/**
 * Apply a league's scoring settings to a projected stat line.
 *
 * Sleeper's `scoring_settings` is a flat `{ statKey: pointsPerUnit }` map, and
 * projection stat lines use the same keys, so scoring is a dot product. Keys
 * present in the stat line but absent from the settings score zero (the league
 * does not count that stat); keys in the settings with no matching stat
 * contribute nothing.
 */
export function scoreStatLine(scoring: Record<string, number>, stats: StatLine): number {
  let total = 0;
  for (const [key, value] of Object.entries(stats)) {
    const weight = scoring[key];
    if (typeof weight === 'number' && Number.isFinite(value)) {
      total += weight * value;
    }
  }
  return round2(total);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Flex definitions as Sleeper spells them in `roster_positions`. */
const FLEX_DEFS: Record<string, Position[]> = {
  FLEX: ['RB', 'WR', 'TE'],
  WRRB_FLEX: ['RB', 'WR'],
  REC_FLEX: ['WR', 'TE'],
  WRRB_WRT: ['RB', 'WR', 'TE'],
  SUPER_FLEX: ['QB', 'RB', 'WR', 'TE'],
  IDP_FLEX: [],
};

const BENCH_SLOTS = new Set(['BN', 'IR', 'TAXI']);

export interface SleeperLeagueLike {
  league_id: string;
  name?: string;
  season?: string;
  total_rosters?: number;
  roster_positions?: string[];
  scoring_settings?: Record<string, number>;
  settings?: Record<string, number>;
}

export interface SleeperDraftLike {
  draft_id: string;
  type?: string;
  settings?: Record<string, number>;
  draft_order?: Record<string, number> | null;
  slot_to_roster_id?: Record<string, number> | null;
}

/**
 * Derive the full league configuration from Sleeper's payloads (FR-2).
 *
 * `overrides` is applied last and wins, which is FR-3: the user can override
 * any derived field and the UI marks it. Returning the override keys alongside
 * the config lets the UI do that marking without re-deriving.
 */
export function deriveLeagueConfig(
  league: SleeperLeagueLike,
  draft: SleeperDraftLike | null,
  opts: { myDraftSlot?: number | null; overrides?: Partial<LeagueConfig> } = {},
): { config: LeagueConfig; overridden: string[] } {
  const roster_positions = league.roster_positions ?? [];
  const scoring_settings = league.scoring_settings ?? {};
  const teams = league.total_rosters ?? 12;

  const starters_by_position: Record<string, number> = {};
  const flexCounts = new Map<string, number>();
  let bench_size = 0;

  for (const slot of roster_positions) {
    if (BENCH_SLOTS.has(slot)) {
      if (slot === 'BN') bench_size++;
      continue;
    }
    if (slot in FLEX_DEFS) {
      flexCounts.set(slot, (flexCounts.get(slot) ?? 0) + 1);
      continue;
    }
    const pos = slot === 'DST' ? 'DEF' : slot;
    starters_by_position[pos] = (starters_by_position[pos] ?? 0) + 1;
  }

  const flex_slots = [...flexCounts.entries()].map(([kind, count]) => ({
    kind,
    eligible: FLEX_DEFS[kind] ?? [],
    count,
  }));

  // Sleeper encodes league type as a number in `settings`: 0 redraft, 1 keeper,
  // 2 dynasty. Anything unexpected reads as redraft, which is the safe default —
  // it costs a dynasty player one click on the source switch, whereas guessing
  // "dynasty" wrongly would re-order a redraft board around aging curves that
  // have no business being applied to it.
  const typeCode = league.settings?.['type'];
  const leagueType: LeagueConfig['league_type'] =
    typeCode === 2 ? 'dynasty' : typeCode === 1 ? 'keeper' : 'redraft';

  // Sleeper has no "this is a rookie draft" flag, so it is inferred from shape.
  // A dynasty *startup* fills every roster slot from scratch and runs as long as
  // the roster is deep; a rookie draft is a handful of rounds bolted onto teams
  // that already exist. Requiring the league to be a keeper or dynasty league
  // first keeps a short redraft league (or a partially-configured one) from
  // being mistaken for a rookie draft.
  const draftRounds = draft?.settings?.['rounds'] ?? roster_positions.length;
  const rookieDraft =
    leagueType !== 'redraft' &&
    draftRounds > 0 &&
    draftRounds <= Math.max(6, Math.floor(roster_positions.length / 2));

  const base: LeagueConfig = {
    league_id: league.league_id,
    name: league.name ?? 'League',
    season: league.season ?? String(new Date().getFullYear()),
    teams,
    roster_positions,
    scoring_settings,
    draft_type: (draft?.type as LeagueConfig['draft_type']) ?? 'snake',
    rounds: draft?.settings?.rounds ?? roster_positions.length,
    draft_slot: opts.myDraftSlot ?? null,
    ppr_value: scoring_settings['rec'] ?? 0,
    te_premium: scoring_settings['bonus_rec_te'] ?? 0,
    superflex: roster_positions.includes('SUPER_FLEX'),
    league_type: leagueType,
    rookie_draft: rookieDraft,
    bench_size,
    starters_by_position,
    flex_slots,
  };

  const overrides = opts.overrides ?? {};
  const overridden = Object.keys(overrides).filter(
    (k) => (overrides as Record<string, unknown>)[k] !== undefined,
  );
  return { config: { ...base, ...overrides }, overridden };
}

export function scoringFormat(scoring: Record<string, number>): ScoringFormat {
  const rec = scoring['rec'];
  if (rec === undefined) return 'unknown';
  if (rec === 0) return 'standard';
  if (rec >= 0.9) return 'ppr';
  if (rec >= 0.4) return 'half-ppr';
  return 'unknown';
}

/**
 * Total starting slots a position can occupy league-wide, counting the share of
 * flex slots it can plausibly claim. This is the denominator for every
 * replacement baseline in `valuation.ts`, so it deliberately lives here next to
 * the roster parsing rather than being re-derived per method.
 *
 * Flex slots are split across eligible positions by a fixed share rather than
 * evenly: in practice RB and WR consume nearly all FLEX slots and TE almost
 * none, and pretending otherwise pushes the TE baseline far too deep.
 */
const FLEX_SHARE: Record<string, Partial<Record<Position, number>>> = {
  FLEX: { RB: 0.45, WR: 0.5, TE: 0.05 },
  WRRB_FLEX: { RB: 0.45, WR: 0.55 },
  REC_FLEX: { WR: 0.85, TE: 0.15 },
  WRRB_WRT: { RB: 0.45, WR: 0.5, TE: 0.05 },
  SUPER_FLEX: { QB: 0.9, RB: 0.03, WR: 0.05, TE: 0.02 },
};

export function startingSlotsByPosition(league: LeagueConfig): Record<Position, number> {
  const out = {} as Record<Position, number>;
  for (const pos of FANTASY_POSITIONS) {
    out[pos] = (league.starters_by_position[pos] ?? 0) * league.teams;
  }
  for (const flex of league.flex_slots) {
    const shares = FLEX_SHARE[flex.kind];
    if (!shares) continue;
    for (const pos of flex.eligible) {
      const share = shares[pos];
      if (!share) continue;
      out[pos] += flex.count * league.teams * share;
    }
  }
  return out;
}

/**
 * Human-readable one-liner describing the format, for the UI header and for
 * `doctor` output. Reads the derived values rather than guessing.
 */
export function describeFormat(league: LeagueConfig): string {
  const parts: string[] = [`${league.teams}-team`];
  const fmt = scoringFormat(league.scoring_settings);
  parts.push(fmt === 'unknown' ? 'custom scoring' : fmt.toUpperCase());
  if (league.te_premium > 0) parts.push(`TE+${league.te_premium}`);
  if (league.superflex) parts.push('superflex');
  parts.push(`${league.rounds} rounds`);
  return parts.join(' · ');
}
